import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'https://blixo-backend-production.up.railway.app/api' });
API.interceptors.request.use(cfg => {
  const token = localStorage.getItem('blixo_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const LABEL_COLORS = [
  '#00ffb4', '#00d4ff', '#ffd700', '#ff6b6b',
  '#7b61ff', '#ff9f43', '#00b894', '#fd79a8',
];

const SUGGESTED = ['Deposit', 'VIP', 'New Customer', 'Follow Up', 'Fraud Risk', 'Active', 'Inactive', 'Prime'];

export default function LabelsSection({ user }) {
  const [labels,     setLabels]     = useState([]);
  const [input,      setInput]      = useState('');
  const [color,      setColor]      = useState('#00ffb4');
  const [showInput,  setShowInput]  = useState(false);

  const loadLabels = useCallback(async () => {
    if (!user?._id) return;
    try {
      const { data } = await API.get(`/labels/${user._id}`);
      setLabels(data);
    } catch (err) { console.error(err); }
  }, [user?._id]);

  useEffect(() => { loadLabels(); }, [loadLabels]);

  const addLabel = async (text, labelColor) => {
    if (!text?.trim()) return;
    try {
      const { data } = await API.post('/labels', {
        userId: user._id,
        text:   text.trim(),
        color:  labelColor || color,
      });
      setLabels(prev => [...prev.filter(l => l._id !== data._id), data]);
      setInput('');
      setShowInput(false);
    } catch (err) { console.error(err); }
  };

  const removeLabel = async (labelId) => {
    try {
      await API.delete(`/labels/${labelId}`);
      setLabels(prev => prev.filter(l => l._id !== labelId));
    } catch (err) { console.error(err); }
  };

  const existingTexts = labels.map(l => l.text.toLowerCase());
  const filteredSuggested = SUGGESTED.filter(s => !existingTexts.includes(s.toLowerCase()));

  return (
    <div style={s.section}>
      {/* Header */}
      <div style={s.header}>
        <span style={s.title}>Labels</span>
        <button style={s.manageBtn} onClick={() => setShowInput(!showInput)}>
          {showInput ? 'Cancel' : '+ Add'}
        </button>
      </div>

      {/* Current labels */}
      <div style={s.labelsWrap}>
        {labels.map(label => (
          <div key={label._id} style={{ ...s.label, borderColor: label.color, color: label.color }}>
            {label.text}
            <button style={{ ...s.removeBtn, color: label.color }} onClick={() => removeLabel(label._id)}>✕</button>
          </div>
        ))}
        {labels.length === 0 && <span style={s.noLabels}>No labels yet</span>}
      </div>

      {/* Add label input */}
      {showInput && (
        <div style={s.addWrap}>
          <input
            style={s.input}
            placeholder="Label name..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addLabel(input, color)}
            autoFocus
            maxLength={30}
          />
          {/* Color picker */}
          <div style={s.colorRow}>
            {LABEL_COLORS.map(c => (
              <button
                key={c}
                style={{ ...s.colorDot, background: c, outline: color === c ? `2px solid ${c}` : 'none', outlineOffset: '2px' }}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
          <button
            style={{ ...s.saveBtn, background: color, opacity: input.trim() ? 1 : 0.4 }}
            onClick={() => addLabel(input, color)}
            disabled={!input.trim()}
          >
            Add Label
          </button>
        </div>
      )}

      {/* Suggested labels */}
      {filteredSuggested.length > 0 && (
        <div style={s.suggestedWrap}>
          <div style={s.suggestedTitle}>Suggested</div>
          <div style={s.suggested}>
            {filteredSuggested.map(text => (
              <button key={text} style={s.suggestedBtn} onClick={() => addLabel(text, '#7b61ff')}>
                + {text}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  section:       { padding: '14px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  header:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  title:         { color: '#e2e8f0', fontSize: '13px', fontWeight: '700', fontFamily: "'Rajdhani',sans-serif", letterSpacing: '1px' },
  manageBtn:     { background: 'transparent', border: '1px solid rgba(123,97,255,0.4)', borderRadius: '7px', color: '#7b61ff', fontSize: '11px', fontWeight: '700', padding: '4px 10px', cursor: 'pointer', letterSpacing: '0.5px' },
  labelsWrap:    { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' },
  label:         { display: 'flex', alignItems: 'center', gap: '5px', padding: '3px 8px 3px 10px', borderRadius: '20px', border: '1px solid', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', background: 'rgba(255,255,255,0.03)' },
  removeBtn:     { background: 'none', border: 'none', cursor: 'pointer', fontSize: '10px', padding: '0', lineHeight: 1, opacity: 0.7 },
  noLabels:      { color: 'rgba(255,255,255,0.2)', fontSize: '12px', fontStyle: 'italic' },
  addWrap:       { display: 'flex', flexDirection: 'column', gap: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '10px', padding: '12px', marginBottom: '8px' },
  input:         { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '8px 12px', color: '#fff', fontSize: '13px', outline: 'none', fontFamily: "'Exo 2',sans-serif" },
  colorRow:      { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  colorDot:      { width: '18px', height: '18px', borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 0 },
  saveBtn:       { border: 'none', borderRadius: '8px', padding: '8px', color: '#080c14', fontSize: '12px', fontWeight: '900', cursor: 'pointer', fontFamily: "'Rajdhani',sans-serif", letterSpacing: '1px' },
  suggestedWrap: { marginTop: '6px' },
  suggestedTitle:{ color: 'rgba(255,255,255,0.25)', fontSize: '10px', letterSpacing: '1px', marginBottom: '6px' },
  suggested:     { display: 'flex', flexWrap: 'wrap', gap: '5px' },
  suggestedBtn:  { background: 'rgba(123,97,255,0.08)', border: '1px solid rgba(123,97,255,0.2)', borderRadius: '15px', color: 'rgba(123,97,255,0.8)', fontSize: '11px', padding: '3px 10px', cursor: 'pointer', fontFamily: "'Exo 2',sans-serif" },
};
